import { useMemo, useState } from "react";
import { Minus, Plus, Sliders, X } from "lucide-react";

export interface UnitOption {
  label: string;
  grams: number;
}

interface Props {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  emoji?: string;
  baseKcal: number;
  baseProtein: number;
  baseCarbs: number;
  baseFat: number;
  /** Grams in 1 base portion. Required to enable unit/grams scaling. */
  gramWeight?: number;
  /** Food-specific named units (tsp, tbsp, cup, etc). When present, the picker shows them. */
  units?: UnitOption[];
  defaultQuantity?: number;
  ctaLabel?: string;
  onConfirm: (scaleFactor: number) => void;
  onCancel: () => void;
  /** Optional: when provided, an "Edit details →" link is shown that opens
   *  the full MealReview screen for per-item editing, the universal unit
   *  wheel, and custom-portion overrides. Called with the current scale
   *  factor so the review opens already reflecting the user's adjustment. */
  onEditDetails?: (scaleFactor: number) => void;
}

const PORTION_CHIPS = [0.5, 1, 1.5, 2, 3];

export default function QuantityConfirm({
  title,
  subtitle,
  imageUrl,
  emoji,
  baseKcal,
  baseProtein,
  baseCarbs,
  baseFat,
  gramWeight,
  units,
  defaultQuantity = 1,
  ctaLabel = "Log it",
  onConfirm,
  onCancel,
  onEditDetails
}: Props) {
  const supportsGrams = typeof gramWeight === "number" && gramWeight > 0;

  // Build the unit options shown in the picker.
  // For foods with named units, include those + Grams (if we have a base gram weight).
  // For foods with just gram data, fall back to "1 portion" + Grams.
  const unitOptions: UnitOption[] = useMemo(() => {
    if (units && units.length > 0) {
      const out: UnitOption[] = [...units];
      if (supportsGrams) out.push({ label: "g", grams: 1 });
      return out;
    }
    if (supportsGrams) {
      return [
        { label: "Portion", grams: gramWeight! },
        { label: "g", grams: 1 }
      ];
    }
    return [];
  }, [units, supportsGrams, gramWeight]);

  // Default to the unit matching the base portion when units are named.
  const defaultUnitIndex = useMemo(() => {
    if (!units || !supportsGrams) return 0;
    const matchIdx = units.findIndex((u) => Math.abs(u.grams - gramWeight!) < 0.5);
    return matchIdx >= 0 ? matchIdx : 0;
  }, [units, supportsGrams, gramWeight]);

  const [unitIdx, setUnitIdx] = useState(defaultUnitIndex);
  const [qty, setQty] = useState(defaultQuantity);

  const selectedUnit = unitOptions[unitIdx];
  const showUnitPicker = unitOptions.length > 1;
  const inGramMode = selectedUnit?.label === "g";

  // scaleFactor = how many "base portions" worth of food this represents.
  // base portion = gramWeight (or 1 if no gram data → quantity is the multiplier)
  const scaleFactor = useMemo(() => {
    if (!selectedUnit) return qty; // no unit data: qty is plain multiplier
    if (!supportsGrams) return qty;
    return (qty * selectedUnit.grams) / gramWeight!;
  }, [qty, selectedUnit, supportsGrams, gramWeight]);

  const step = inGramMode ? 5 : 0.25;
  const minQty = inGramMode ? 1 : 0.25;
  const inputSuffix = inGramMode ? "g" : "";

  function stepBy(delta: number) {
    setQty((q) => Math.max(minQty, Math.round((q + delta) * 100) / 100));
  }

  function selectUnit(idx: number) {
    const prev = unitOptions[unitIdx];
    const next = unitOptions[idx];
    setUnitIdx(idx);
    // Sensible default qty when switching units:
    //   switching to grams → start at the base portion's grams
    //   switching from grams to a named unit → reset to 1
    if (next.label === "g") {
      setQty(prev && prev.label !== "g" ? Math.round(prev.grams * qty) : gramWeight ?? 100);
    } else {
      setQty(1);
    }
  }

  const previewGrams = supportsGrams ? Math.round(scaleFactor * gramWeight!) : null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 border-t border-slate-800 rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-md md:max-w-2xl lg:max-w-3xl mx-auto p-4 space-y-4">
          {/* Drag handle + close */}
          <div className="flex items-center justify-between relative">
            <div className="h-1 w-10 rounded-full bg-slate-700 mx-auto" />
            <button
              onClick={onCancel}
              className="absolute right-0 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-800 flex items-center justify-center"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Item */}
          <div className="flex items-center gap-3">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-800 flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center text-2xl flex-shrink-0">
                {emoji ?? "🍽️"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-slate-100 truncate">{title}</div>
              {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
            </div>
          </div>

          {/* Unit picker */}
          {showUnitPicker && (
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-1.5">Unit</div>
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                {unitOptions.map((u, i) => (
                  <button
                    key={`${u.label}-${i}`}
                    type="button"
                    onClick={() => selectUnit(i)}
                    className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                      i === unitIdx
                        ? "bg-emerald-950 border border-emerald-700 text-emerald-100"
                        : "bg-slate-800 border border-slate-700 text-slate-300"
                    }`}
                  >
                    {u.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">
                {inGramMode ? "Weight" : "Quantity"}
              </span>
              {supportsGrams && previewGrams !== null && (
                <span className="text-[11px] text-slate-400 tabular-nums">
                  ≈ {previewGrams}g total
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepBy(-step)}
                className="w-10 h-10 rounded-lg bg-slate-800 text-slate-200 flex items-center justify-center active:bg-slate-700"
                aria-label="Decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <div className="flex-1 relative">
                <input
                  type="number"
                  inputMode="decimal"
                  step={step}
                  min={minQty}
                  value={qty}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (Number.isFinite(v) && v > 0) setQty(v);
                    else if (e.target.value === "") setQty(0);
                  }}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-center text-slate-100 text-xl font-semibold tabular-nums focus:outline-none focus:border-emerald-500"
                />
                {inputSuffix && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">
                    {inputSuffix}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => stepBy(step)}
                className="w-10 h-10 rounded-lg bg-slate-800 text-slate-200 flex items-center justify-center active:bg-slate-700"
                aria-label="Increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {!inGramMode && (
              <div className="flex gap-1.5 mt-2">
                {PORTION_CHIPS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQty(q)}
                    className={`flex-1 py-1.5 rounded text-xs ${
                      qty === q
                        ? "bg-slate-700 text-emerald-400 font-semibold"
                        : "bg-slate-800/60 text-slate-400"
                    }`}
                  >
                    ×{q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Totals preview */}
          <div className="p-3 rounded-lg bg-slate-800/60">
            <div className="flex items-baseline justify-between">
              <div className="text-3xl font-bold text-slate-100 tabular-nums">
                {Math.round(baseKcal * scaleFactor)}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">kcal</div>
            </div>
            <div className="flex gap-3 text-xs mt-1.5">
              <span className="text-sky-400 tabular-nums">P {Math.round(baseProtein * scaleFactor)}</span>
              <span className="text-amber-400 tabular-nums">C {Math.round(baseCarbs * scaleFactor)}</span>
              <span className="text-violet-400 tabular-nums">F {Math.round(baseFat * scaleFactor)}</span>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={onCancel}
              className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(scaleFactor)}
              disabled={qty <= 0 || scaleFactor <= 0}
              className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
            >
              {ctaLabel}
            </button>
          </div>

          {/* Escape hatch to MealReview for per-item editing, the universal
              unit wheel (g ↔ oz ↔ cup ↔ tbsp ↔ tsp ↔ servings — Path 2),
              and custom portion text. Only shown when the parent wired up
              onEditDetails, so call sites that don't need the full review
              (like quick-logging a single generic) stay simple. */}
          {onEditDetails && (
            <button
              type="button"
              onClick={() => onEditDetails(scaleFactor)}
              className="w-full mt-1 py-2 text-xs text-slate-400 hover:text-emerald-400 flex items-center justify-center gap-1.5"
            >
              <Sliders className="w-3.5 h-3.5" />
              Edit details · units, per-item, macros
            </button>
          )}
        </div>
      </div>
    </>
  );
}
